import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Badge, Button, Card, EmptyState, LoadingList, SectionHeader, cx,
} from '@timely/ui';
import {
    DEFAULT_FILTERS, DEFAULT_SORT, SETTLEMENT_LABELS, SORT_LABELS,
    activeFilterCount, bookRow, bookTotals, filtersFromParams, holds,
    type BookFilters, type BookRow, type BookSort, type SettlementFilter,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useBook, type ServerTotals } from '../book/useBook';
import { useFormatters } from '../book/useFormatters';

/**
 * The book: every account this person may see, and the one screen they work
 * from.
 *
 * Two things shape it. The first is that **it is a worklist, not a table
 * dump** — it opens on what is work (accounts with dues), and a row is one
 * press away from the account.
 *
 * The second is that no figure on it claims to be something it is not. The
 * filters, the sort and the paging are all arguments to `book_page`, so the
 * list is of the whole filtered book and the headline is the aggregate over
 * that same population — never of the page that happens to be on screen. This
 * file decides nothing about which accounts qualify.
 *
 * Which accounts a person may see at all is decided by `can_see_customer()`
 * in SQL, on the page and on the aggregate alike: a collector receives their
 * own accounts and a total of their own accounts, and deleting every line of
 * this file would not change that.
 *
 * Filters live in the address bar, so a back press from an account returns to
 * the list as it was, and a filtered book can be sent to a colleague.
 */
export function Book() {
    const context = useActiveWorkspace();
    const { money, count, today } = useFormatters();

    const [params, setParams] = useSearchParams();
    const filters = filtersFromParams(params);
    const sort = (params.get('sort') as BookSort) ?? DEFAULT_SORT;

    const {
        customers, members, totals: serverTotals, loading, loadingMore, error,
        reload, loadMore, hasMore, loadedCount,
    } = useBook(context, { filters, sort });

    const memberNames = useMemo(
        () => new Map(members.map(m => [m.memberId, m.displayName || m.code || 'Somebody'])),
        [members],
    );

    // Presentation only: the badges, the ageing figures and the follow-up
    // wording for the rows the database returned. No filtering and no sorting
    // here — both already happened, over accounts this screen never saw.
    // Against the company's day, not the browser's. The database filtered by
    // the company's; a badge worked out from the browser's would disagree with
    // the list it sits in for anybody not sitting in the company's timezone.
    const rows = useMemo(() => customers.map(c => bookRow(c, today)), [customers, today]);
    const onScreen = useMemo(() => bookTotals(rows), [rows]);

    const set = (changes: Partial<BookFilters> & { sort?: BookSort }) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            const isDefault =
                value === '' || value === false
                || (key === 'settlement' && value === DEFAULT_FILTERS.settlement)
                || (key === 'sort' && value === DEFAULT_SORT)
                || value === 'any';
            if (isDefault) next.delete(key); else next.set(key, String(value));
        }
        setParams(next, { replace: true });
    };

    const narrowed = activeFilterCount(filters);

    return (
        <Page>
            <SectionHeader
                title="The book"
                subtitle="Every account you can see, and what is owed on it."
                actions={
                    <BookSummary
                        serverTotals={serverTotals}
                        onScreen={{ count: onScreen.count, outstanding: onScreen.outstanding }}
                        loadedCount={loadedCount}
                        loading={loading}
                    />
                }
            />

            <Filters
                filters={filters}
                sort={sort}
                members={members}
                narrowed={narrowed}
                onChange={set}
                onClear={() => setParams(new URLSearchParams(), { replace: true })}
            />

            {onScreen.count > 0 && (
                <div className="mt-4 flex items-center gap-4 flex-wrap text-[12.5px] text-label-3">
                    {/* Explicitly of the rows on screen, and it says so. A
                        glance at the page somebody is reading, not a claim
                        about the book: the headline above is that claim. */}
                    <span className="text-label-3">Of the {count(onScreen.count)} shown:</span>
                    <span>
                        <strong className="text-label num">{money.compact(onScreen.over90)}</strong> over 90 days
                    </span>
                    <span><strong className="text-label num">{count(onScreen.dueToday)}</strong> due today</span>
                    <span><strong className="text-label num">{count(onScreen.overdue)}</strong> overdue</span>
                    {onScreen.unassigned > 0 && (
                        <span><strong className="text-label num">{count(onScreen.unassigned)}</strong> unassigned</span>
                    )}
                </div>
            )}

            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading the book" rows={8} />
                ) : error ? (
                    <div className="px-5 py-6">
                        <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                ) : serverTotals !== null && serverTotals.visibleCount === 0 ? (
                    <EmptyState
                        title="No accounts yet"
                        hint={holds(context, 'datasource.manage')
                            ? 'Connect your receivables sheet and sync it, and your book appears here.'
                            : 'Once somebody connects your receivables sheet, your accounts appear here.'}
                        action={holds(context, 'datasource.manage')
                            ? (
                                <Link
                                    to={`/w/${context.workspace.slug}/data`}
                                    className="text-accent font-semibold hover:underline"
                                >
                                    Connect a sheet
                                </Link>
                            )
                            : undefined}
                    />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Nothing matches"
                        hint={serverTotals
                            ? `None of your ${count(serverTotals.visibleCount)} accounts match what you are looking for.`
                            : 'No account matches what you are looking for.'}
                        action={
                            <Button variant="secondary" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                                Clear filters
                            </Button>
                        }
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {rows.map(row => (
                            <BookRowItem
                                key={row.customer.id}
                                row={row}
                                slug={context.workspace.slug}
                                search={params.toString()}
                                ownerName={nameFor(row, memberNames)}
                            />
                        ))}
                    </ul>
                )}
            </Card>

            {hasMore && !loading && (
                <div className="mt-5 text-center">
                    <Button variant="secondary" disabled={loadingMore} onClick={() => { void loadMore(); }}>
                        {loadingMore ? 'Loading…' : serverTotals
                            ? `Load more (${count(loadedCount)} of ${count(serverTotals.matchedCount)})`
                            : `Load more (${count(loadedCount)} so far)`}
                    </Button>
                </div>
            )}
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

const nameFor = (row: BookRow, names: Map<string, string>): string | null => {
    const owner = row.customer.ownerMemberId as string | undefined;
    const collector = row.customer.collectorMemberId as string | undefined;
    const who = collector ?? owner;
    if (!who) return null;
    return names.get(who) ?? null;
};

const FOLLOW_UP_TONE = {
    today: 'brand', overdue: 'dang', future: 'neutral',
    completed: 'pos', no_follow_up: 'neutral',
} as const;

const FOLLOW_UP_LABEL = {
    today: 'Due today', overdue: 'Overdue', future: 'Scheduled',
    completed: 'Collected', no_follow_up: 'No follow-up',
} as const;

function BookRowItem({
    row, slug, search, ownerName,
}: {
    row: BookRow; slug: string; search: string; ownerName: string | null;
}) {
    const { money, date } = useFormatters();
    const c = row.customer;
    // The list's own state travels with the link, so a back press returns to
    // the book as it was rather than to an unfiltered one.
    const to = `/w/${slug}/book/${c.id}${search ? `?${search}` : ''}`;

    return (
        <li>
            {/* The whole row is the link, so the target is the row and not a
                word in it — and it is a real anchor, so it opens in a new tab,
                takes focus in order, and works from the keyboard. */}
            <Link
                to={to}
                className={cx(
                    'flex items-start gap-4 px-5 py-3.5 no-underline',
                    'hover:bg-hover focus-visible:bg-hover outline-none',
                    'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
                )}
            >
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14.5px] font-semibold text-label truncate">{c.company}</span>
                        {c.isUrgent && <Badge tone="dang">Urgent</Badge>}
                        {row.badDebt && <Badge tone="dang">Bad debt</Badge>}
                        {row.settled && <Badge tone="pos">Settled</Badge>}
                        {c.totalType === 'Cr' && <Badge tone="warn">In credit</Badge>}
                    </div>
                    <div className="text-[12.5px] text-label-3 mt-0.5 truncate">
                        {c.sourceKey && <span className="num">{c.sourceKey}</span>}
                        {c.sourceKey && (c.city || ownerName) && ' · '}
                        {c.city}
                        {c.city && ownerName && ' · '}
                        {ownerName}
                        {!c.sourceKey && !c.city && !ownerName && (c.contactPerson || '—')}
                    </div>
                </div>

                <div className="flex-none text-right">
                    <div
                        className="text-[14.5px] font-semibold text-label num tabular-nums"
                        title={money.balance(c.total, c.totalType)}
                    >
                        {money.compact(c.total)}
                    </div>
                    {row.overdue.over90 > 0 && (
                        <div className="text-[12px] text-dang num">
                            {money.compact(row.overdue.over90)} over 90d
                        </div>
                    )}
                </div>

                <div className="flex-none w-[116px] text-right max-sm:hidden">
                    <Badge tone={FOLLOW_UP_TONE[row.followUp]}>{FOLLOW_UP_LABEL[row.followUp]}</Badge>
                    {c.followUpDate && (
                        <div className="text-[12px] text-label-3 mt-1">
                            <time dateTime={new Date(c.followUpDate).toISOString()}>
                                {date.short(c.followUpDate)}
                            </time>
                        </div>
                    )}
                </div>
            </Link>
        </li>
    );
}

/* -------------------------------------------------------------------------- */

function Filters({
    filters, sort, members, narrowed, onChange, onClear,
}: {
    filters: BookFilters;
    sort: BookSort;
    members: { memberId: string; displayName: string; code: string | null }[];
    narrowed: number;
    onChange: (changes: Partial<BookFilters> & { sort?: BookSort }) => void;
    onClear: () => void;
}) {
    const [open, setOpen] = useState(false);

    return (
        <Card className="mt-5 px-4 py-3.5">
            <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <label htmlFor="book-search" className="block text-[12.5px] font-semibold text-label mb-1">
                        Search
                    </label>
                    <input
                        id="book-search"
                        type="search"
                        value={filters.search}
                        placeholder="Name, code, phone, city"
                        onChange={e => onChange({ search: e.target.value })}
                        className="w-full h-10 px-3.5 rounded-[10px] bg-card border border-separator-strong text-[14px] text-label outline-none focus:border-accent"
                    />
                </div>

                <Choice
                    id="book-settlement" label="Showing" value={filters.settlement}
                    options={(Object.keys(SETTLEMENT_LABELS) as SettlementFilter[])
                        .map(value => ({ value, label: SETTLEMENT_LABELS[value] }))}
                    onChange={value => onChange({ settlement: value as SettlementFilter })}
                />

                <Choice
                    id="book-sort" label="Sort by" value={sort}
                    options={(Object.keys(SORT_LABELS) as BookSort[])
                        .map(value => ({ value, label: SORT_LABELS[value] }))}
                    onChange={value => onChange({ sort: value as BookSort })}
                />

                <div className="mb-[1px]">
                    <Button
                        size="sm" variant="secondary"
                        aria-expanded={open}
                        aria-controls="book-more-filters"
                        onClick={() => setOpen(v => !v)}
                    >
                        {open ? 'Fewer filters' : 'More filters'}
                        {narrowed > 0 && <span className="ml-1.5 num">({narrowed})</span>}
                    </Button>
                </div>

                {narrowed > 0 && (
                    <div className="mb-[1px]">
                        <Button size="sm" variant="quiet" onClick={onClear}>Clear</Button>
                    </div>
                )}
            </div>

            <div id="book-more-filters" hidden={!open} className="mt-3 pt-3 border-t border-separator">
                <div className="flex items-end gap-3 flex-wrap">
                    <Choice
                        id="book-followup" label="Follow-up" value={filters.followUp}
                        options={[
                            { value: 'any', label: 'Any' },
                            { value: 'today', label: 'Due today' },
                            { value: 'overdue', label: 'Overdue' },
                            { value: 'future', label: 'Scheduled' },
                            { value: 'no_follow_up', label: 'None set' },
                            { value: 'completed', label: 'Collected' },
                        ]}
                        onChange={value => onChange({ followUp: value as BookFilters['followUp'] })}
                    />
                    <Choice
                        id="book-ageing" label="Older than" value={filters.ageing}
                        options={[
                            { value: 'any', label: 'Any age' },
                            { value: 'over45', label: '45 days' },
                            { value: 'over90', label: '90 days' },
                            { value: 'over135', label: '135 days' },
                        ]}
                        onChange={value => onChange({ ageing: value as BookFilters['ageing'] })}
                    />
                    <Choice
                        id="book-owner" label="Assigned to" value={filters.owner}
                        options={[
                            { value: 'any', label: 'Anyone' },
                            { value: 'mine', label: 'Me' },
                            { value: 'unassigned', label: 'Nobody' },
                            ...members.map(m => ({
                                value: m.memberId,
                                label: m.displayName || m.code || 'Somebody',
                            })),
                        ]}
                        onChange={value => onChange({ owner: value })}
                    />
                    <Choice
                        id="book-rank" label="Rank" value={filters.rank}
                        options={[
                            { value: 'any', label: 'Any' },
                            { value: 'Good', label: 'Good' },
                            { value: 'Late', label: 'Late' },
                            { value: 'Bad', label: 'Bad debt' },
                        ]}
                        onChange={value => onChange({ rank: value as BookFilters['rank'] })}
                    />
                    <label className="flex items-center gap-2 mb-2.5 text-[13.5px] text-label">
                        <input
                            type="checkbox"
                            checked={filters.urgentOnly}
                            onChange={e => onChange({ urgentOnly: e.target.checked })}
                        />
                        Urgent only
                    </label>
                </div>
            </div>
        </Card>
    );
}

function Choice({
    id, label, value, options, onChange,
}: {
    id: string; label: string; value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-[12.5px] font-semibold text-label mb-1">{label}</label>
            <select
                id={id} value={value} onChange={e => onChange(e.target.value)}
                className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
            >
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}

/**
 * The figure at the top of the book, and what it is allowed to claim.
 *
 * A financial product must never show a page subtotal where a receivables
 * total belongs. Session 6.5 made that safe by labelling: where a filter was
 * outside what the aggregate could express, the screen showed the sum of the
 * rows on screen and said so.
 *
 * Session 6.6 removed the need for it. Every filter now runs in the database
 * over the caller's whole visible book, so `book_totals` answers the same
 * question the list is showing and the headline is a real total whatever is
 * filtered. The only reason the second shape survives is that a request can
 * fail: when the aggregate did not arrive, the screen still says "Visible
 * amount" rather than putting the word "total" over a number that is not one.
 */
function BookSummary({
    serverTotals,
    onScreen,
    loadedCount,
    loading,
}: {
    serverTotals: ServerTotals | null;
    onScreen: { count: number; outstanding: number };
    loadedCount: number;
    loading: boolean;
}) {
    const { money, count } = useFormatters();

    // Not yet answered is not the same as failed. Before the first answer the
    // header says it is working, not "₹0" and "we could not total your book".
    if (!serverTotals && loading) {
        return (
            <div className="text-right" role="status">
                <div className="text-[12px] text-label-3">Totalling your book…</div>
            </div>
        );
    }

    if (serverTotals) {
        const shown = loadedCount > 0 && loadedCount < serverTotals.matchedCount;
        return (
            <div className="text-right">
                <div className="text-[17px] font-bold text-label num tabular-nums">
                    {money.exact(serverTotals.outstanding)}
                </div>
                <div className="text-[12px] text-label-3">
                    Total outstanding ·{' '}
                    {count(serverTotals.matchedCount)}{' '}
                    {serverTotals.matchedCount === 1 ? 'account' : 'accounts'}
                    {serverTotals.matchedCount !== serverTotals.visibleCount && (
                        <> of {count(serverTotals.visibleCount)}</>
                    )}
                </div>
                {/* How much of the answer is on screen. The figure above is of
                    all of it either way, which is the whole point. */}
                {shown && (
                    <div className="text-[11.5px] text-label-3 mt-0.5">
                        Showing {count(loadedCount)} of {count(serverTotals.matchedCount)}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="text-right">
            <div className="text-[17px] font-bold text-label num tabular-nums">
                {money.exact(onScreen.outstanding)}
            </div>
            <div className="text-[12px] text-label-3">
                {/* Never "total". This is the sum of what is on screen, and it
                    says so and says how much that is. */}
                Visible amount · {count(onScreen.count)}{' '}
                {onScreen.count === 1 ? 'account' : 'accounts'} shown
            </div>
            <div className="text-[11.5px] text-warn mt-0.5 max-w-[260px]">
                We could not total your whole book just now, so this is the amount on screen.
            </div>
        </div>
    );
}
